import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AppBarWithButtons } from '../../../../components/designe/app-bars/app-bar-with-action-buttons/AppBarWithButtons';
import AppLanguageAvatar from '../../../../components/designe/app-entity-avatar/app-language-avatar/AppExerciseAvatar';
import AppSwitch from '../../../../components/designe/app-switch/AppSwitch';
import AppFormSelectWrapper from '../../../../components/designe/forms/app-form-select-wrapper';
import { useNavigationStack } from '../../../../context/NavigationStackContext';
import { useUnsavedChangesGuard } from '../../../../hooks/useUnsavedChangesGuard';
import { useGetLanguages } from '../../../../redux/api/translationsAPI';
import {
  AccessScope,
  AvatarSize,
  AvatarTypes,
  PostsInteractionScope,
  PrimaryColor,
  ThemeMode,
} from '../../../../shared/constants';
import { Paths } from '../../../../shared/paths';
import { PRIMARY_COLOR_MAP } from '../../../../shared/primary-color-map';
import { ILanguage } from '../../../../types/interfaces/language';
import { useStyles } from './UserConfigEdit.styles';
import { useUserConfigEditComponent } from './useUserConfigEditComponent';

export default function UserConfigEdit(props) {
  const { data: languages } = useGetLanguages();
  const { t } = useTranslation();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { formik, isLoading } = useUserConfigEditComponent();
  const { replace } = useNavigationStack();
  const { allowAndLeave, dialog } = useUnsavedChangesGuard({
    isDirty: formik.dirty && !formik.isSubmitting,
    leaveAction: () => replace(Paths.settings.toConfigView),
  });

  function getItem(language: ILanguage) {
    return (
      <Box display="flex" gap={1} alignItems="center">
        <AppLanguageAvatar
          language={language}
          size={AvatarSize.s}
          type={AvatarTypes.round}
        />
        <Typography variant="text-md">{language && language.title}</Typography>
      </Box>
    );
  }

  function getPagePrivacyOptions() {
    return Object.values(AccessScope).map((scope) => ({
      id: scope,
      name: t(`settings.pagePrivacy_${scope}`),
    }));
  }

  function getPostPrivacyOptions() {
    return Object.values(PostsInteractionScope).map((scope) => ({
      id: scope,
      name: t(`settings.postPrivacy_${scope}`),
    }));
  }

  function getCommentPrivacyOptions() {
    return Object.values(PostsInteractionScope).map((scope) => ({
      id: scope,
      name: t(`settings.commentPrivacy_${scope}`),
    }));
  }

  const pagePrivacyOptions = getPagePrivacyOptions();
  const postPrivacyOptions = getPostPrivacyOptions();
  const commentPrivacyOptions = getCommentPrivacyOptions();

  return (
    <div className={classes.userConfigWrapper}>
      {dialog}
      <form onSubmit={formik.handleSubmit} className={classes.userConfigForm}>
        <Box className={classes.userConfigContent}>
          <Box className={classes.colorSection}>
            <Typography variant="text-md">
              {t('settings.visualization.primaryColor')}
            </Typography>
            <Box className={classes.colorCirclesRow}>
              {(Object.values(PrimaryColor) as PrimaryColor[]).map((color) => (
                <Box
                  key={color}
                  className={`${classes.colorCircle} ${
                    formik.values.primaryColor === color
                      ? classes.colorCircleSelected
                      : ''
                  }`}
                  sx={{ backgroundColor: PRIMARY_COLOR_MAP[color].main }}
                  onClick={() => formik.setFieldValue('primaryColor', color)}
                  role="button"
                  aria-pressed={formik.values.primaryColor === color}
                  aria-label={color}
                />
              ))}
            </Box>
          </Box>

          <Box className={classes.themeRow}>
            <Typography variant="text-md">
              {t('settings.visualization.theme')}
            </Typography>
            <AppSwitch
              value={formik.values.theme === ThemeMode.light}
              onChange={() =>
                formik.setFieldValue(
                  'theme',
                  formik.values.theme === ThemeMode.light
                    ? ThemeMode.dark
                    : ThemeMode.light,
                )
              }
            />
          </Box>

          <Box>
            <AppFormSelectWrapper
              label={t('exercises.language')}
              className={classes.selectorWithContent}
              options={languages}
              fieldName="title"
              disableClear
              disableSearch
              defaultValue={languages?.find(
                (e) => e.id === formik.values.language?.id,
              )}
              name="language"
              formik={formik}
              renderOption={getItem}
            />
          </Box>
          <Box>
            <AppFormSelectWrapper
              label={t('settings.pagePrivacy')}
              options={pagePrivacyOptions}
              disableClear
              disableSearch
              defaultValue={pagePrivacyOptions?.find(
                (e) => e.id === formik.values.scope,
              )}
              onChange={(e) =>
                formik.setFieldValue('scope', e.target.value?.id)
              }
              name="scope"
              formik={formik}
            />
          </Box>
          <Box>
            <AppFormSelectWrapper
              label={t('settings.whoCanAddPost')}
              options={postPrivacyOptions}
              disableClear
              disableSearch
              defaultValue={postPrivacyOptions?.find(
                (e) => e.id === formik.values.postsScope,
              )}
              onChange={(e) =>
                formik.setFieldValue('postsScope', e.target.value?.id)
              }
              name="postsScope"
              formik={formik}
            />
          </Box>
          <Box>
            <AppFormSelectWrapper
              label={t('settings.whoCanComment')}
              options={commentPrivacyOptions}
              disableClear
              disableSearch
              defaultValue={commentPrivacyOptions?.find(
                (e) => e.id === formik.values.commentsScope,
              )}
              onChange={(e) =>
                formik.setFieldValue('commentsScope', e.target.value?.id)
              }
              name="commentsScope"
              formik={formik}
            />
          </Box>
        </Box>
        <Box>
          <Box className={classes.buttonWrapper}>
            <AppBarWithButtons
              loading={isLoading}
              disabled={!formik.dirty}
              onCancelClick={allowAndLeave}
            />
          </Box>
        </Box>
      </form>
    </div>
  );
}
